import React from 'react';

import { Box, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { DroxyAccordion } from 'components/Droxy/DroxyAccordion';
import { DroxyAccordionSection } from 'components/Droxy/DroxyAccordionSection';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { SectionNumber } from 'components/SectionNumber';
import { useUserContext } from 'contexts/UserContext';
import { t } from 'i18next';
import { ToolConfigurationPanelWrapper } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolConfigurationPanelWrapper';
import { ToolStatusSection } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolStatusSection';
import { useLocation } from 'react-router-dom';
import { TakeoverRequestToolConfig } from 'shared/interfaces/tools/TakeoverRequest';
import { ToolConfig, ToolKind, ToolTrigger } from 'shared/interfaces/tools/Tool';
import { getToolConfig } from 'shared/interfaces/tools/utils';
import { formHasFieldErrors } from 'shared/utils/form';
import { emailValidator, requiredInputValidator } from 'shared/utils/form-validators';

enum TakeoverRequestPanelSections {
  GENERAL_SETTINGS = 'general-settings'
}

interface TakeoverRequestToolConfigurationPanelProps {
  onUpdate: (toolConfigs: ToolConfig[]) => void;
  onBack: () => void;
}

export function TakeoverRequestToolConfigurationPanel({
  onUpdate,
  onBack
}: TakeoverRequestToolConfigurationPanelProps) {
  const [user] = useUserContext();
  const location = useLocation();
  const toolConfigs: ToolConfig[] = location.state?.toolConfigs ?? [];
  const maybeExistingToolConfig = getToolConfig(toolConfigs, ToolKind.TAKEOVER_REQUEST);
  const isNewToolConfig = !maybeExistingToolConfig;

  const takeoverRequestToolConfigForm = useForm<TakeoverRequestToolConfig>({
    initialValues: {
      _id: maybeExistingToolConfig?._id,
      kind: ToolKind.TAKEOVER_REQUEST as const,
      toolName: ToolKind.TAKEOVER_REQUEST as const,
      trigger: ToolTrigger.INTENT as const,
      enabled: maybeExistingToolConfig?.enabled ?? true,
      email: maybeExistingToolConfig?.email || user.email,
      notificationChannelType: maybeExistingToolConfig?.notificationChannelType ?? 'email'
    },
    validate: {
      email: (value) => requiredInputValidator(value) || emailValidator(value)
    }
  });

  const handleUpdate = () => {
    const validation = takeoverRequestToolConfigForm.validate();
    if (validation.hasErrors) return;
    onUpdate([takeoverRequestToolConfigForm.values]);
  };

  return (
    <ToolConfigurationPanelWrapper
      onSave={handleUpdate}
      onBack={onBack}
      onCancel={isNewToolConfig ? onBack : () => takeoverRequestToolConfigForm.reset()}
      hasChanges={takeoverRequestToolConfigForm.isDirty() || isNewToolConfig}
      px={0}>
      <Box>
        <ToolStatusSection
          enabled={takeoverRequestToolConfigForm.values.enabled}
          onToggle={(enabled) => takeoverRequestToolConfigForm.setFieldValue('enabled', enabled)}
        />
      </Box>
      <Box>
        <DroxyAccordion defaultOpenSections={[TakeoverRequestPanelSections.GENERAL_SETTINGS]}>
          <DroxyAccordionSection
            title={t('SMART_ASSISTANT.TOOLS.TAKEOVER_REQUEST.SECTIONS.GENERAL_SETTINGS.TITLE')}
            section={TakeoverRequestPanelSections.GENERAL_SETTINGS}
            icon={<SectionNumber number={1} />}
            hasError={formHasFieldErrors(takeoverRequestToolConfigForm, 'email')}>
            <Stack spacing="md">
              <TextInput
                label={
                  <InputLabelWithInfo
                    label={t('SMART_ASSISTANT.TOOLS.TAKEOVER_REQUEST.EMAIL.LABEL')}
                    infoContentTranslationKey="SMART_ASSISTANT.TOOLS.TAKEOVER_REQUEST.EMAIL.INFO"
                  />
                }
                placeholder={t('SMART_ASSISTANT.TOOLS.TAKEOVER_REQUEST.EMAIL.PLACEHOLDER')}
                maxLength={100}
                {...takeoverRequestToolConfigForm.getInputProps('email')}
              />
            </Stack>
          </DroxyAccordionSection>
        </DroxyAccordion>
      </Box>
    </ToolConfigurationPanelWrapper>
  );
}
